import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { ArrowRight, Eye, Wrench, ClipboardCheck, CalendarDays } from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Label } from "../components/ui/label";

export default function Checkout() {
  const { customer, patchCustomer } = useApp();
  const nav = useNavigate();
  const [seeLive, setSeeLive] = useState(false);
  const [tileLaying, setTileLaying] = useState(false);
  const [tools, setTools] = useState([]);
  const [busy, setBusy] = useState(false);
  const [apptDate, setApptDate] = useState(null);
  const [apptNotes, setApptNotes] = useState("");
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { category: "Tools" } }).then(r => setTools(r.data)).catch(() => {});
  }, []);

  // Load availability whenever "See Live Tile" is opened
  useEffect(() => {
    if (!seeLive) return;
    api.get("/appointments/availability").then(r => {
      setBlockedDates((r.data.blocked_dates || []).map(d => new Date(d + "T00:00:00")));
    }).catch(() => {});
  }, [seeLive]);

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);
  const maxDate = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + 60); return d; }, []);

  if (!customer) return <div className="p-20 text-center text-[#8A8A8A]">Please register first.</div>;

  const isDateBlocked = (d) => {
    if (d < today || d > maxDate) return true;
    // Sundays closed
    if (d.getDay() === 0) return true;
    const iso = d.toISOString().slice(0, 10);
    return blockedDates.some(b => b.toISOString().slice(0, 10) === iso);
  };

  const submitForCheck = async () => {
    if (seeLive && !apptDate) { toast.error("Pick a date for your showroom visit"); return; }
    setBusy(true);
    try {
      await patchCustomer({ current_step: "payment", see_live_tile: seeLive, tile_laying_service: tileLaying });
      const { data: order } = await api.post("/orders", {
        customer_id: customer.id,
        tile_laying_service: tileLaying,
        see_live_tile: seeLive,
      });
      if (seeLive && apptDate) {
        try {
          const iso = apptDate.toISOString().slice(0, 10);
          await api.post("/appointments", {
            customer_id: customer.id,
            customer_name: customer.name,
            customer_whatsapp: customer.whatsapp,
            order_id: order.id,
            date: iso,
            notes: apptNotes || null,
          });
          toast.success(`Showroom appointment booked for ${apptDate.toDateString()}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Could not book appointment (order still submitted)");
        }
      } else {
        toast.success("Sent to admin for stock check");
      }
      nav(`/orders/${order.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit request");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 md:px-12 py-16" data-testid="checkout-page">
      <p className="label-eyebrow">Final Step</p>
      <h1 className="font-editorial text-5xl sm:text-6xl tracking-editorial mt-3 mb-12">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-12">
          <section>
            <h3 className="font-editorial text-2xl mb-4">Delivery to</h3>
            <div className="border-l-2 border-[#1E2C5A] pl-6 py-2">
              <p className="font-medium">{customer.name}</p>
              <p className="text-sm text-[#5C5C5C] mt-1">WhatsApp {customer.whatsapp}</p>
              <p className="text-sm text-[#5C5C5C]">{customer.location}</p>
            </div>
          </section>

          <section>
            <h3 className="font-editorial text-2xl mb-4">Premium options</h3>

            <div className="border-t border-[#1A1A1A]/10">
              <label className="flex items-start gap-4 py-6 cursor-pointer" data-testid="see-live-tile">
                <input type="checkbox" checked={seeLive} onChange={(e) => setSeeLive(e.target.checked)} className="mt-1.5 accent-[#1E2C5A]" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#1E2C5A]" strokeWidth={1.5} />
                    <p className="font-medium">See Live Tile</p>
                    <span className="label-eyebrow !text-[#1E2C5A]">Premium</span>
                  </div>
                  <p className="text-sm text-[#5C5C5C] mt-1">Book a showroom slot to see your selected tiles in person before completing your purchase.</p>
                </div>
              </label>
              {seeLive && (
                <div className="pb-6 pl-8 space-y-4" data-testid="see-live-calendar">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-[#1E2C5A]" strokeWidth={1.5} />
                    <p className="label-eyebrow !text-[#1E2C5A]">Choose an appointment date</p>
                  </div>
                  <div className="border border-[#1A1A1A]/10 bg-white inline-block">
                    <Calendar
                      mode="single"
                      selected={apptDate}
                      onSelect={setApptDate}
                      disabled={isDateBlocked}
                      fromDate={today}
                      toDate={maxDate}
                    />
                  </div>
                  {apptDate && (
                    <div className="text-sm text-[#1A1A1A]" data-testid="appt-selected">
                      Selected: <span className="font-medium">{apptDate.toDateString()}</span>
                    </div>
                  )}
                  <div className="max-w-lg">
                    <Label htmlFor="appt-notes" className="label-eyebrow">Notes for the team (optional)</Label>
                    <textarea
                      id="appt-notes"
                      rows={2}
                      value={apptNotes}
                      onChange={(e) => setApptNotes(e.target.value)}
                      placeholder="e.g. Interested in marble collections. Coming with my architect."
                      className="w-full border border-[#1A1A1A]/15 p-2 text-sm outline-none focus:border-[#1E2C5A] resize-none mt-1"
                      data-testid="appt-notes"
                    />
                  </div>
                  <p className="text-xs text-[#8A8A8A]">Sundays and admin-blocked dates are greyed out. Slots up to 60 days from today.</p>
                </div>
              )}
            </div>

            <label className="flex items-start gap-4 py-6 border-t border-b border-[#1A1A1A]/10 cursor-pointer" data-testid="need-tile-laying">
              <input type="checkbox" checked={tileLaying} onChange={(e) => setTileLaying(e.target.checked)} className="mt-1.5 accent-[#1E2C5A]" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-[#1E2C5A]" strokeWidth={1.5} />
                  <p className="font-medium">Need Tile Laying Service?</p>
                </div>
                <p className="text-sm text-[#5C5C5C] mt-1">We&apos;ll send our Top 5 vetted tile layers to your WhatsApp with contact details.</p>
              </div>
            </label>
          </section>

          {tileLaying && tools.length > 0 && (
            <section data-testid="recommended-tools">
              <h3 className="font-editorial text-2xl mb-4">Recommended tools for installation</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {tools.map(t => (
                  <div key={t.id} className="flex gap-4 border border-[#1A1A1A]/10 p-4">
                    <div className="w-20 h-20 bg-[#F0EFEA] flex-shrink-0">
                      <img src={t.image_url} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="font-medium">{t.name}</p>
                      <p className="text-xs text-[#8A8A8A]">{t.size}</p>
                      <p className="text-sm text-[#1E2C5A] mt-1">₹{t.offer_price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="bg-[#F0EFEA] p-10 h-fit sticky top-32" data-testid="checkout-summary">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardCheck className="w-4 h-4 text-[#1E2C5A]" strokeWidth={1.5} />
            <p className="label-eyebrow">Step 1 of 2</p>
          </div>
          <h3 className="font-editorial text-3xl mt-2 mb-4">Check Availability</h3>
          <p className="text-xs text-[#5C5C5C] mb-6 leading-relaxed">
            Send this cart to our admin. Once we&apos;ve confirmed stock and any lead times, you&apos;ll be able to review the response and pay online — no upfront payment yet.
          </p>

          <div className="text-xs text-[#5C5C5C] space-y-2 mb-6 border-t border-[#1A1A1A]/10 pt-4">
            <p className="flex justify-between"><span>1. Admin checks stock</span><span className="text-[#8A8A8A]">usually within a day</span></p>
            <p className="flex justify-between"><span>2. Review updated cart</span><span className="text-[#8A8A8A]">tick to proceed</span></p>
            <p className="flex justify-between"><span>3. Pay online</span><span className="text-[#8A8A8A]">Razorpay · UPI/Card</span></p>
          </div>

          <button onClick={submitForCheck} disabled={busy} className="btn-clay w-full" data-testid="check-availability-btn">
            {busy ? "Sending..." : "Check Availability"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </div>
  );
}
